import {
  getCanRedeemLuxPlusBenefits,
  getHasLuxPlusInAnyRegion,
  getIsFreePreviewLuxPlusMember,
  getIsLuxPlusEnabled,
} from 'luxPlus/selectors/featureToggle'
import { useMemo } from 'react'
import { useAppSelector } from 'hooks/reduxHooks'
import useOfferList from 'hooks/Offers/useOfferList'
import { excludeNullOrUndefined } from 'checkout/utils'
import { getDestinationIds } from 'lib/search/filterIdUtils'
import getValueOffPercent from 'lib/offer/getValueOffPercent'
import { without } from 'lib/array/arrayUtils'

const HIDDEN_PREVIEW_OFFER_LIMIT = 10
const HIDDEN_PREVIEW_FALLBACK_SAVING_PERCENT = 62
const HIDDEN_PREVIEW_SCHEDULE_VISIBILITIES: NonNullable<App.OfferListFilters['scheduleVisibilities']> = [
  'luxPlusEarlyAccessVisible',
  'luxPlusExclusiveVisible',
]

interface Options {
  listFilters: App.OfferListFilters
  mainOfferIds: Array<string>
  excludedOfferIds?: Array<string>
  mainSearchBlocked: boolean
}

interface HiddenPreviewBannerEligibility {
  canActivateExperiment: boolean
  firstDestinationId?: string
  hiddenPreviewOfferIds: Array<string>
  savingPercent: number
}

function getFirstDestinationId(filters: App.OfferListFilters): string | undefined {
  if (
    filters.propertyId ||
    filters.landmarkId ||
    filters.landmarkIds?.length ||
    filters.bounds ||
    filters.lat !== undefined ||
    filters.lng !== undefined
  ) {
    return undefined
  }

  const firstDestinationId = getDestinationIds(filters)?.[0]
  return firstDestinationId
}

function getSavingPercent(metaData: App.OfferListMetaData): number {
  const pricing = metaData.pricing
  const value = pricing?.lowestPriceValue
  const comparisonPrice = pricing?.lowestMemberPrice && pricing.lowestMemberPrice > 0 ?
    pricing.lowestMemberPrice :
    pricing?.lowestPrice

  return value && comparisonPrice && value > comparisonPrice ? getValueOffPercent(value, comparisonPrice) : 0
}

function getSavingPercentForMetaData(metaData: Array<App.OfferListMetaData>): number {
  const savingPercent = Math.max(
    0,
    ...metaData.slice(0, HIDDEN_PREVIEW_OFFER_LIMIT).map(getSavingPercent),
  )

  return savingPercent || HIDDEN_PREVIEW_FALLBACK_SAVING_PERCENT
}

function useHiddenPreviewBannerEligibility({
  listFilters,
  mainOfferIds,
  excludedOfferIds,
  mainSearchBlocked,
}: Options): HiddenPreviewBannerEligibility {
  const luxPlusEnabled = useAppSelector(getIsLuxPlusEnabled)
  const freePreviewMember = useAppSelector(getIsFreePreviewLuxPlusMember)
  const canRedeemLuxPlusBenefits = useAppSelector(getCanRedeemLuxPlusBenefits)
  const hasPaidLuxPlusMembership = useAppSelector(getHasLuxPlusInAnyRegion)

  const firstDestinationId = useMemo(
    () => getFirstDestinationId(listFilters),
    [listFilters],
  )
  const visibleMainOfferIds = useMemo(
    () => excludedOfferIds ? without(mainOfferIds, ...excludedOfferIds) : mainOfferIds,
    [excludedOfferIds, mainOfferIds],
  )
  const hasVisibleOffers = useMemo(
    () => visibleMainOfferIds.length > 0,
    [visibleMainOfferIds],
  )
  const canJoinFreePreview = !freePreviewMember && !canRedeemLuxPlusBenefits && !hasPaidLuxPlusMembership

  const hiddenPreviewOfferListFilters = useMemo(() => {
    const offerTypes = listFilters.offerTypes
    if (
      !luxPlusEnabled ||
      !canJoinFreePreview ||
      !firstDestinationId ||
      !hasVisibleOffers ||
      !offerTypes?.length ||
      mainSearchBlocked
    ) {
      return undefined
    }

    return {
      destinationId: firstDestinationId,
      limit: HIDDEN_PREVIEW_OFFER_LIMIT,
      offerTypes,
      scheduleVisibilities: HIDDEN_PREVIEW_SCHEDULE_VISIBILITIES,
    }
  }, [
    canJoinFreePreview,
    firstDestinationId,
    hasVisibleOffers,
    listFilters.offerTypes,
    luxPlusEnabled,
    mainSearchBlocked,
  ])

  const hiddenPreviewOfferList = useOfferList(hiddenPreviewOfferListFilters, {
    disabled: !hiddenPreviewOfferListFilters,
  })

  const hiddenPreviewOfferMetaData = useAppSelector(state =>
    hiddenPreviewOfferList.key ?
      state.offer.searchResultMetadata.offerMetaData[hiddenPreviewOfferList.key] :
      undefined,
  )

  const hiddenPreviewMetaData = useMemo(() => (
    hiddenPreviewOfferList.offerIds
      .map(offerId => hiddenPreviewOfferMetaData?.[offerId])
      .filter(excludeNullOrUndefined)
  ), [hiddenPreviewOfferList.offerIds, hiddenPreviewOfferMetaData])

  const savingPercent = useMemo(
    () => getSavingPercentForMetaData(hiddenPreviewMetaData),
    [hiddenPreviewMetaData],
  )

  return useMemo(() => ({
    canActivateExperiment: !hiddenPreviewOfferList.fetching && hiddenPreviewOfferList.offerIds.length > 0,
    firstDestinationId,
    hiddenPreviewOfferIds: hiddenPreviewOfferList.offerIds,
    savingPercent,
  }), [
    firstDestinationId,
    hiddenPreviewOfferList.fetching,
    hiddenPreviewOfferList.offerIds,
    savingPercent,
  ])
}

export default useHiddenPreviewBannerEligibility
